import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { usePlatformStore } from "@platform/store";
import { useFetchUserPrograms } from "@core/features/program";
import { Loader } from "@themes/pollinator/ui/Loader";
import { OuterContentContainer } from "@themes/pollinator/ui/OuterContentContainer";
import { SectionHeading } from "@themes/pollinator/ui/SectionHeading";
import { AppPageHeader } from "@themes/pollinator/ui/AppPageHeader";
import { Type } from "@themes/pollinator/ui/Type";
import { Button } from "@themes/pollinator/ui/Button";
import moment from "moment";

export function DashboardPage() {
  const navigate = useNavigate();
  const user = usePlatformStore((s) => s.user);
  const isHydrated = usePlatformStore((s) => s.isHydrated);

  const { data, isLoading } = useFetchUserPrograms({
    user,
    isHydrated,
  });

  const programs = data?.programs ?? [];
  const programCount = programs.length;

  // Redirect to single program if user has exactly one
  useEffect(() => {
    if (!isLoading && programCount === 1) {
      navigate(`/dashboard/${programs[0].id}`, { replace: true });
    }
  }, [isLoading, programCount, programs, navigate]);

  // Show loader while fetching or if redirecting (single program)
  if (isLoading || programCount === 1) {
    return <Loader isFullPage />;
  }

  // No programs - show message
  if (programCount === 0) {
    return (
      <>
        <AppPageHeader
          title="Dashboard"
          description="View and manage your programs."
        />
        <OuterContentContainer className="flex flex-1" innerClassName="gap-8">
          <div className="w-full flex flex-col gap-6 pb-8 max-w-3xl">
            <SectionHeading
              title="No Programs Found"
              description="You are not currently enrolled in any programs. Check back later or contact admin@pollinator.coop for support."
              titleVariant="h3"
            />
          </div>
        </OuterContentContainer>
      </>
    );
  }

  // Multiple programs - show program list
  return (
    <>
      <AppPageHeader
        title="Dashboard"
        description="View and manage your programs."
      />
      <OuterContentContainer className="flex flex-1" innerClassName="gap-8">
        <div className="w-full flex flex-col gap-6 pb-8 max-w-3xl">
          <SectionHeading title="Your Programs" titleVariant="h3">
            <div className="flex flex-col gap-6">
              {programs.map((program) => (
                <div key={program.id} className="flex flex-col gap-2">
                  <Type as="h3" variant="h4">
                    {program.name}
                  </Type>
                  {program.description && (
                    <Type as="p" variant="body2">
                      {program.description}
                    </Type>
                  )}
                  {(program.start_date || program.end_date) && (
                    <Type as="p" variant="caption">
                      {program.start_date &&
                        `Starts: ${moment(program.start_date).format("MMMM D, YYYY")}`}
                      {program.start_date && program.end_date && " · "}
                      {program.end_date &&
                        `Ends: ${moment(program.end_date).format("MMMM D, YYYY")}`}
                    </Type>
                  )}
                  <Button
                    variant="secondary"
                    className="mt-1 shrink-0 w-fit"
                    onClick={() => navigate(`/dashboard/${program.id}`)}
                  >
                    View Program
                  </Button>
                </div>
              ))}
            </div>
          </SectionHeading>
        </div>
      </OuterContentContainer>
    </>
  );
}

export default DashboardPage;


